<script setup>
  import { computed, watchEffect } from 'vue';
  import { useUserStore } from "@/stores/UserStore";
  import { useMapDrawingStore } from '@/stores/MapDrawingStore';
  import { useUnitConverter } from '@/composables/useUnitConverter'
  import MapDrawingShapeDistanceMarker from '@/components/MapDrawingShapeDistanceMarker.vue';

  const props = defineProps({
    drawingManager: {
      type: Object,
      required: true
    },
    shape: {
      type: Object,
      required: true
    },
    showDistanceMarkers: {
      type: Boolean,
      default: true
    },
    showHeadingsMarkers: {
      type: Boolean,
      default: false
    },
    activeShape: {
      type: Boolean,
      required: true,
    },
  });

  const userStore = useUserStore();
  const mapDrawingStore = useMapDrawingStore();
  const unitConverter = useUnitConverter();

  const distanceMarkers = computed(() => {
    if (props.shape.latLons.length < 2 || (!props.showDistanceMarkers && !props.showHeadingsMarkers)) return [];

    const markers = [];

    for (let i = 0; i < props.shape.latLons.length - 1; i++) {
      const heading = props.drawingManager.getHeadingBetweenLatLons(props.shape.latLons[i], props.shape.latLons[i + 1]).toFixed(1);
      const midPointLatLon = google.maps.geometry.spherical.interpolate(props.shape.latLons[i], props.shape.latLons[i + 1], 0.5);
      const midPoint = props.drawingManager.getPointFromLatLon(midPointLatLon);
      const distance = google.maps.geometry.spherical.computeDistanceBetween(props.shape.latLons[i], props.shape.latLons[i + 1]);
      const distanceInUsersUnits = getDistanceInUsersUnits(distance);

      markers.push({
        heading,
        midPoint,
        distance: distanceInUsersUnits.toFixed(1),
        prefix: props.shape.type === 'circle' ? 'R' : null,
        id: `distance-marker-${props.shape.latLons[i].id}-${props.shape.latLons[i + 1].id}-${i}`,
      });
    }

    if (props.shape.closed && !props.shape.type.includes('circle')) {
      const closedSegmentHeading = props.drawingManager.getHeadingBetweenLatLons(props.shape.latLons[props.shape.latLons.length - 1], props.shape.latLons[0]).toFixed(1);
      const closedSegmentMidPointLatLon = google.maps.geometry.spherical.interpolate(props.shape.latLons[0], props.shape.latLons[props.shape.latLons.length - 1], 0.5);
      const closedSegmentMidPoint = props.drawingManager.getPointFromLatLon(closedSegmentMidPointLatLon);
      const distance = google.maps.geometry.spherical.computeDistanceBetween(props.shape.latLons[0], props.shape.latLons[props.shape.latLons.length - 1]);
      const distanceInUsersUnits = getDistanceInUsersUnits(distance);

      markers.push({
        heading: closedSegmentHeading,
        midPoint: closedSegmentMidPoint,
        distance: distanceInUsersUnits.toFixed(1),
        id: `distance-marker-${props.shape.latLons[0].id}-${props.shape.latLons[props.shape.latLons.length - 1].id}`,
      });
    }
    
    return markers;
  })

  const displayType = computed(() => props.showDistanceMarkers ? 'distance' : 'heading');

  const getDistanceInUsersUnits = (distance) => unitConverter.convert('m', userStore.units.distance, distance, false);

  watchEffect(() => {
    if (distanceMarkers.value.length < 2) return;

    const totalDistance = distanceMarkers.value.reduce((acc, marker) => acc + parseFloat(marker.distance), 0);

    mapDrawingStore.setMetadata(props.shape.id, 'totalDistance', `${totalDistance.toFixed(1)}${userStore.units.distance}`);
  });
</script>

<template>
  <MapDrawingShapeDistanceMarker
    v-for="(marker, index) in distanceMarkers"
    :marker="marker"
    :units="userStore.units.distance"
    :type="displayType"
    :index="index"
    :key="marker.id"
  />
</template>